<template>
    <div>
        <rkm-form
            v-if="!readonly"
            ref="RkmForm"
        >
            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-date-picker
                        :min-date="data.additionals.paciente.dataNascimento"
                        :validations="validations.medidoEm"
                        label="Data da medição anterior"
                        v-model="model.medidoEm"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        :validations="validations.peso"
                        @change="calcularIMC"
                        @input="handlePesoInput"
                        append="kg"
                        label="Peso"
                        v-mask="masks.peso"
                        v-model="model.peso"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        :validations="validations.altura"
                        @change="calcularIMC"
                        append="cm"
                        label="Altura"
                        placeholder="000"
                        v-mask="masks.altura"
                        v-model="model.altura"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        disabled
                        label="IMC"
                        v-model="model.imc.imc"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-input
                        :validations="validations.perimetroCefalico"
                        append="cm"
                        label="Perímetro cefálico"
                        placeholder="000.0"
                        v-mask="masks.perimetroCefalico"
                        v-model="model.perimetroCefalico"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        :validations="validations.circunferenciaAbdominal"
                        append="cm"
                        label="Circunferência abdominal"
                        placeholder="000"
                        v-mask="masks.circunferenciaAbdominal"
                        v-model="model.circunferenciaAbdominal"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        :validations="validations.perimetroPanturrilha"
                        append="cm"
                        label="Perímetro da panturrilha"
                        placeholder="00"
                        v-mask="masks.perimetroPanturrilha"
                        v-model="model.perimetroPanturrilha"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-button
                        :label="false"
                        @click="store"

                        color="primary"
                    >
                        Incluir
                    </rkm-button>

                    <rkm-button
                        :label="false"
                        @click="resetForm"
                    >
                        Limpar
                    </rkm-button>
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-table
            :fields="fields"
            :items="items"
            hide-filters
            hide-pagination
        >
            <template v-slot:cell(actions)="{item}">
                <rkm-button
                    :disabled="!!item.id"
                    @click="destroy(item)"
                    color="danger"
                    small
                    title="Excluir"
                >
                    <rkm-icon icon="trash" />
                </rkm-button>
            </template>
        </rkm-table>

        <rkm-row class="mt-2">
            <rkm-col cols="12">
                <rkm-form-select
                    :options="data.form.antropometria.faixasEtarias"
                    label="Faixa etária"
                    v-model="model.faixaEtaria"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col cols="12">
                <rkm-tabs>
                    <rkm-tab title="Peso">
                        <rkm-chart-scatter
                            :chart-data="pesoChartData"
                            :chart-options="chartOptions.peso"
                        />
                    </rkm-tab>

                    <rkm-tab title="Altura">
                        <rkm-chart-scatter
                            :chart-data="alturaChartData"
                            :chart-options="chartOptions.altura"
                        />
                    </rkm-tab>

                    <rkm-tab title="IMC">
                        <rkm-chart-scatter
                            :chart-data="imcChartData"
                            :chart-options="chartOptions.imc"
                        />
                    </rkm-tab>

                    <rkm-tab title="Perímetro cefálico">
                        <rkm-chart-scatter
                            :chart-data="perimetroCefalicoChartData"
                            :chart-options="chartOptions.perimetroCefalico"
                        />
                    </rkm-tab>
                </rkm-tabs>
            </rkm-col>
        </rkm-row>
    </div>
</template>

<script>
// Enums.
import unidadeMedida from '@/enums/cadastro/unidadeMedida'

// Mixins.
import {error, object, repository, stringCase} from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import {mapActions} from 'vuex'

export default {
    computed: {
        items() {
            return this.data.form.antropometrias.concat(this.data.model.antropometrias)
        },
        selectedItemsByFaixaEtaria() {
            const data = []

            this.items.forEach(antropometria => {
                const medidoEm = this.others.moment(antropometria.medidoEm, 'DD/MM/YYYY')
                const dataNascimento = this.others.moment(this.data.additionals.paciente.dataNascimento, 'DD/MM/YYYY')
                const meses = medidoEm.diff(dataNascimento, 'months', true)

                if (meses >= this.faixaEtariaEmMeses.min && meses <= this.faixaEtariaEmMeses.max) {
                    data.push({
                        ...antropometria,
                        meses,
                    })
                }
            })

            return data
        },
        validations() {
            return {
                medidoEm: {required: true},
                peso: {
                    required: !this.model.altura
                        && !this.model.perimetroCefalico
                        && !this.model.circunferenciaAbdominal
                        && !this.model.perimetroPanturrilha,
                    min_value: 0.5,
                    max_value: 500,
                },
                altura: {
                    required: !this.model.peso
                        && !this.model.perimetroCefalico
                        && !this.model.circunferenciaAbdominal
                        && !this.model.perimetroPanturrilha,
                    min_value: 20,
                    max_value: 250,
                },
                perimetroCefalico: {
                    required: !this.model.peso
                        && !this.model.altura
                        && !this.model.circunferenciaAbdominal
                        && !this.model.perimetroPanturrilha,
                    min_value: 10,
                    max_value: 200,
                },
                circunferenciaAbdominal: {
                    required: !this.model.peso
                        && !this.model.altura
                        && !this.model.perimetroCefalico
                        && !this.model.perimetroPanturrilha,
                    max_value: 999,
                },
                perimetroPanturrilha: {
                    required: !this.model.peso
                        && !this.model.altura
                        && !this.model.perimetroCefalico
                        && !this.model.circunferenciaAbdominal,
                    min_value: 10,
                    max_value: 99,
                },
            }
        },
        chartOptions() {
            const getIndexsToHideLabel = item =>  {

                const indexes = []

                item.datasets.forEach((data, index) => {

                    if ([-1, 0, null].includes(data.zScore)) {

                        indexes.push(index)
                    }
                })

                return indexes
            }

            return {
                peso: this.getChartDefaultOptions(
                    this.pesoData.scales,
                    'peso',
                    getIndexsToHideLabel(this.pesoChartData)
                ),
                altura: this.getChartDefaultOptions(
                    this.alturaData.scales,
                    'altura',
                    getIndexsToHideLabel(this.alturaChartData)
                ),
                imc: this.getChartDefaultOptions(
                    this.imcData.scales,
                    'imc',
                    getIndexsToHideLabel(this.imcChartData)
                ),
                perimetroCefalico: this.getChartDefaultOptions(
                    this.perimetroCefalicoData.scales,
                    'perimetroCefalico',
                    getIndexsToHideLabel(this.perimetroCefalicoChartData)
                ),
            }
        },
        pesoChartData() {
            return this.getDatasets(this.pesoData, 'peso')
        },
        alturaChartData() {
            return this.getDatasets(this.alturaData, 'altura')
        },
        imcChartData() {
            return this.getDatasets(this.imcData, 'imc')
        },
        perimetroCefalicoChartData() {
            return this.getDatasets(this.perimetroCefalicoData, 'perimetroCefalico')
        },
        pesoData() {
            return this.data.form.antropometria.grafico.peso.find(p => p.faixaEtaria === this.model.faixaEtaria).data
        },
        alturaData() {
            return this.data.form.antropometria.grafico.altura.find(a => a.faixaEtaria === this.model.faixaEtaria).data
        },
        imcData() {
            return this.data.form.antropometria.grafico.imc.find(i => i.faixaEtaria === this.model.faixaEtaria).data
        },
        perimetroCefalicoData() {
            return this.data.form.antropometria.grafico.perimetroCefalico.find(pc => pc.faixaEtaria === this.model.faixaEtaria).data
        },
        faixaEtariaEmMeses() {
            const faixaEtaria = this.data.form.antropometria.faixasEtarias.find(fe => fe.value === this.model.faixaEtaria)

            return {min: faixaEtaria.min * 12, max: faixaEtaria.max * 12}
        },
        fields() {

            const fields = [
                {key: 'medidoEm', label: 'Data da medição', sortable: true},
                {key: 'peso', label: 'Peso (kg)', sortable: true},
                {key: 'altura', label: 'Altura (cm)', sortable: true},
                {key: 'imc', label: 'IMC (kg/m²)', sortable: true},
                {key: 'perimetroCefalico', label: 'Perímetro cefálico (cm)', sortable: true},
                {key: 'circunferenciaAbdominal', label: 'Circunferência abdominal (cm)', sortable: true},
                {key: 'perimetroPanturrilha', label: 'Perímetro da panturrilha (cm)', sortable: true},
            ]

            if (!this.readonly) {

                fields.push({class: 'col-min text-center', key: 'actions', label: 'Ações'})
            }

            return fields
        }
    },
    created() {
        this.repositories = {
            geral: this.getConectaSusRepository('geral'),
        }
    },
    data: () => ({
        enums: {unidadeMedida},
        masks: {
            altura: '###',
            circunferenciaAbdominal: '###',
            perimetroCefalico: '###.#',
            perimetroPanturrilha: '##',
            peso: '###.##',
        },
        model: {
            medidoEm: null,
            peso: null,
            altura: null,
            imc: {
                imc: null,
                classificacao: null,
            },
            perimetroCefalico: null,
            circunferenciaAbdominal: null,
            perimetroPanturrilha: null,
            faixaEtaria: '0-2',
        },
        others: {moment},
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        store() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    this.data.model.antropometrias.push({
                        medidoEm: this.others.moment(this.model.medidoEm).format('DD/MM/YYYY'),
                        peso: this.model.peso,
                        altura: this.model.altura,
                        imc: this.model.imc.imc,
                        perimetroCefalico: this.model.perimetroCefalico,
                        circunferenciaAbdominal: this.model.circunferenciaAbdominal,
                        perimetroPanturrilha: this.model.perimetroPanturrilha,
                    })

                    this.resetForm()
                })
        },
        destroy(item) {
            this.showDeleteConfirmation('Deseja realmente excluir a antropometria?', async () => {
                this.data.model.antropometrias.splice(this.data.model.antropometrias.indexOf(item), 1)
            })
        },
        resetForm() {
            this.model.medidoEm = null
            this.model.peso = null
            this.model.altura = null

            this.model.imc = {
                imc: null,
                classificacao: null
            }

            this.model.perimetroCefalico = null
            this.model.circunferenciaAbdominal = null
            this.model.perimetroPanturrilha = null

            this.$nextTick().then(() => this.$refs.RkmForm.resetValidator())
        },
        handlePesoInput(value) {
            const length = value.replace('.', '').length

            if (length <= 3) {
                this.masks.peso = '#.##'
            }
            else if (length <= 4) {
                this.masks.peso = '##.##'
            }
            else {
                this.masks.peso = '###.##'
            }
        },
        calcularIMC() {
            if (!this.model.peso || !this.model.altura) {
                this.model.imc.imc = null
                this.model.imc.classificacao = null
            } else {
                this.setLoading()

                const request = {
                    unidadeMedidaMassaId: this.enums.unidadeMedida.QUILOGRAMAS,
                    massa: this.model.peso,
                    unidadeMedidaAlturaId: this.enums.unidadeMedida.CENTIMETROS,
                    altura: this.model.altura,
                }

                this.repositories.geral.calcularIMC(this.camelObjectKeysToSnakeRecursive(request))
                    .then(response => {
                        this.model.imc = response.data
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
        },
        getChartDefaultOptions(scales, yScaleName, indexesToHideLabel = []) {
            const y = {
                min: this.getMinYScale(scales, yScaleName),
                max: this.getMaxYScale(scales, yScaleName),
                ticks: {
                    autoSkip: false,
                    stepSize: scales.y.stepSize,
                },
                title: {
                    display: true,
                    font: {weight: 'bold'},
                    text: scales.y.title,
                },
            }

            return {
                animation: false,
                aspectRatio: 2,
                maintainAspectRatio: false,
                plugins: {
                    decimation: {enabled: true},
                    legend: {
                        labels: {
                            filter: function(item) {
                                return !indexesToHideLabel.includes(item.datasetIndex)
                            },
                        },
                        onClick: null,
                    },
                },
                spanGaps: true,
                scales: {
                    x: {
                        min: scales.x.min,
                        max: scales.x.max,
                        ticks: {
                            autoSkip: false,
                            callback(value) {
                                if (value > 0 && value % 12 === 0) {
                                    value = value / 12

                                    return `${value} ${value > 1 ? ' anos' : 'ano'}`
                                }
                                else if (value > 12) {
                                    return value % 12
                                }
                                else {
                                    return value
                                }
                            },
                            font(font) {
                                if (font.tick && font.tick.value % 12 === 0) return {weight: 'bold'}
                            },
                            stepSize: scales.x.stepSize,
                        },
                        title: {
                            display: true,
                            font: {
                                weight: 'bold',
                            },
                            text: scales.x.title,
                        },
                    },
                    y: {
                        ...y,
                        position: 'left',
                    },
                    y2: {
                        ...y,
                        position: 'right',
                    },
                },
            }
        },
        getDatasets(item, yScaleName) {
            const maxYScale = this.getMaxYScale(item.scales, yScaleName)

            const datasets = item.zScores.map(zScore => {
                let color = null
                let label = null
                let fill = null
                let showLine = true

                if (zScore.zScore === -3) {
                    color = '#000000'
                    label = 'Muito baixo'
                    fill = 'start'
                }
                else if (zScore.zScore === -2) {
                    color = '#FF0000'
                    label = 'Baixo'
                    fill = '-1'
                }
                else if (zScore.zScore === -1) {
                    color = '#00FF00'
                    label = 'Normal'
                    fill = '-1'
                    showLine = false
                }
                else if (zScore.zScore === 0) {
                    color = '#00FF00'
                    label = 'Normal'
                    fill = '-1'
                    showLine = false
                }
                else if (zScore.zScore === 1) {
                    color = '#00FF00'
                    label = 'Normal'
                    fill = '-1'
                }
                else if (zScore.zScore === 2) {
                    color = '#FF0000'
                    label = 'Alto'
                    fill = '-1'
                }
                else if (zScore.zScore === 3) {
                    color = '#000000'
                    label = 'Muito alto'
                    fill = '-1'
                    showLine = false

                    zScore.axis = zScore.axis.map(axis => {
                        return {
                            x: axis.x,
                            y: maxYScale,
                        }
                    })
                }

                return {
                    zScore: zScore.zScore,
                    backgroundColor: `${color}22`,
                    borderColor: color,
                    borderDash: [5,5],
                    borderWidth: 1,
                    data: zScore.axis,
                    fill,
                    label: label,
                    pointHitRadius: 0,
                    pointRadius: 0,
                    showLine,
                }
            })

            const items = this.clone(this.selectedItemsByFaixaEtaria)
                .filter(item => item[yScaleName] !== null)

            items.sort((a, b) => {
                const aDate = this.others.moment(a.medidoEm, 'DD/MM/YYYY').toDate()
                const bDate = this.others.moment(b.medidoEm, 'DD/MM/YYYY').toDate()

                return aDate - bDate
            })

            datasets.push({
                zScore: null,
                backgroundColor: "#000000",
                borderColor: "#000000",
                borderWidth: 1,
                data: items.map(antropometria => {
                    return {
                        x: antropometria.meses,
                        y: antropometria[yScaleName],
                    }
                }),
                fill: false,
                label: 'Medição',
                showLine: true,
            })

            return {datasets}
        },
        getMinYScale(scales, yScaleName) {
            const antropometrias = this.selectedItemsByFaixaEtaria.filter(item => item[yScaleName] !== null)

            let minYScale = scales.y.stepSize === 1
                ? Math.floor(Math.min(...antropometrias.map(antropometria => antropometria[yScaleName]))) - scales.y.stepSize
                : Math.floor(Math.min(...antropometrias.map(antropometria => antropometria[yScaleName])) / scales.y.stepSize) * scales.y.stepSize

            if (minYScale < 0) {
                minYScale = 0
            }

            return minYScale < scales.y.min ? minYScale : scales.y.min
        },
        getMaxYScale(scales, yScaleName) {
            const antropometrias = this.selectedItemsByFaixaEtaria.filter(item => item[yScaleName] !== null)

            const maxYScale = scales.y.stepSize === 1
                ? Math.ceil(Math.max(...antropometrias.map(antropometria => antropometria[yScaleName]))) + scales.y.stepSize
                : Math.ceil(Math.max(...antropometrias.map(antropometria => antropometria[yScaleName])) / scales.y.stepSize) * scales.y.stepSize

            return maxYScale > scales.y.max ? maxYScale : scales.y.max
        },
    },
    mixins: [error, object, repository, stringCase],
    props: {
        data: {required: true, type: Object},
        readonly: {type: Boolean, default: false}
    },
}
</script>